import { useEffect, useState } from 'react';

import ClassIcon from '@mui/icons-material/Class';
import { Button } from '@mui/material';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { RouterParams } from 'containers/AppRouter';
import ButtonActionDelete from 'shared/components/Button/Action/ButtonActionDelete/ButtonActionDelete';
import Loader from 'shared/containers/Loader';
import { ViewDetailsRenderValue } from 'shared/containers/ViewDetails/components/RenderDefaultValue';
import ViewDetails, {
  GENERAL_INFO_TITLE, SETTINGS_TITLE, STATUS_INFO_TITLE, ViewDetailsT,
} from 'shared/containers/ViewDetails/ViewDetails';
import { APIErrorT } from 'shared/models/api';
import { SourcesResultT } from 'shared/models/sources';
import SourcesForm, { SourcesFormFieldT, SourcesIdentifiers } from 'shared/models/sources-form';
import api from 'utils/api';
import { formatDateFromNow } from 'utils/date';
import { useEntityNameForNav } from 'utils/hooks/providers/entity-name-for-nav-context';
import jsonObj from 'utils/objects';
import { generateRandomString } from 'utils/string';

import './SourcesDetails.scss';

function SourcesDetails() {
  const params: RouterParams = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [dataDetailsCommon, setdataDetailsCommon] = useState<SourcesResultT | null>(null);
  const [dataDetailsSpecific, setdataDetailsSpecific] = useState<any>({});
  const [sourceFormSpecific, setSourceFormSpecific] = useState<Array<any>>([]);
  const history = useHistory();

  const { setEntityNameForNav } = useEntityNameForNav();

  const dataStructure: ViewDetailsT = {
    sections: [
      {
        title: STATUS_INFO_TITLE,
        renderSubtitleFn: (data: SourcesResultT) => (data.statusReportedAt ? `Updated ${formatDateFromNow(data.statusReportedAt)}` : ''),
        items: [
          {
            type: 'status',
            label: 'Downlink (Tx)',
            dataPath: 'downlinkStatus',
          },
          {
            type: 'status',
            label: 'Uplink (Rx)',
            dataPath: 'uplinkStatus',
          },
        ],
        shape: 2,
      },
      {
        title: GENERAL_INFO_TITLE,
        items: [
          {
            type: 'name',
          },
          {
            type: 'creation',
          },
          {
            label: 'Type',
            icon: <ClassIcon color="primary" />,
            externalValue: SourcesIdentifiers
              && SourcesIdentifiers[dataDetailsCommon?.source as string]?.displayName,
          },
          {
            type: 'update',
          },
        ],
        shape: 2,
      },
    ],
  };

  const loadData = () => {
    api.sources.getOne(params.id as string)
      .then((resp: Response) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
              const redirectTo = '/sources';
              console.warn('Failed to get item - Redirect on', redirectTo);
              history.push(redirectTo);
            });
        } else {
          resp.json()
            .then((data: any) => {
              setdataDetailsCommon(data);
              setEntityNameForNav(data.name);
              try {
                const { parametersGroupName } = SourcesIdentifiers[data?.source];
                const specific = SourcesForm[data?.source];
                if (parametersGroupName) {
                  setdataDetailsSpecific(data[parametersGroupName]);
                  setSourceFormSpecific(specific);
                }
              } catch (error) {
                console.warn(`${data?.source} parameters group is not managed`);
              }
              setIsLoading(false);
            });
        }
      });
  };

  useEffect(() => {
    loadData();
  }, []);

  if (isLoading) {
    return <Loader.Page />;
  }

  return (
    <div>
      <div className="title__header">
        <h1 className="text-first-letter-uppercase">Source details</h1>
        <div className="title__header--actions">
          <Link to={`/sources/${params.id}/edit`}>
            <Button variant="contained" color="secondary">Edit</Button>
          </Link>
          <ButtonActionDelete
            targetId={params.id as string}
            api={api.sources}
            messageComplement={`this source named '${dataDetailsCommon?.name}'`}
            redirectLocation="/sources"
          />
        </div>
      </div>
      <div className="sources-item">
        <ViewDetails dataStructure={dataStructure} data={dataDetailsCommon} />
        <div className="display-details__section">
          <h3>{ SETTINGS_TITLE }</h3>
          <ViewDetailsRenderValue
            name="Default MTU for downlink"
            value={dataDetailsCommon?.mtuTxLink}
          />
          <ViewDetailsRenderValue
            name="Disables dynamic MTU"
            value={dataDetailsCommon?.useDefaultMtu}
          />
          {
            sourceFormSpecific
              .map((data: SourcesFormFieldT) => (
                <ViewDetailsRenderValue
                  key={generateRandomString()}
                  name={data.displayName}
                  value={jsonObj.getDescendantProp(dataDetailsSpecific, data.name)}
                  width="70rem"
                />
              ))
          }
        </div>
      </div>
    </div>
  );
}

export default SourcesDetails;
